/**
 * SDMS Template Builder — New / Edit Data Validation rule (shared <AppDialog>), opened from
 * the Data Validation Rules Manager. Excel's "Data Validation" dialog in one page: the
 * cells, what is allowed, the input message and the error alert. Nothing is written until
 * the template is saved.
 */
import { useEffect, useState } from 'react';
import { AppDialog } from '../../../components/AppDialog';
import { absoluteSqref, normalizeSqref } from './cfModel';
import {
  dateToSerial, DV_ERROR_STYLES, DV_OPERATORS, DV_TYPES, listFormula, listSourceText, serialToDate, type DvDefinition,
} from './dvModel';

/** Excel's own limits (the server checks them too). */
const MAX_TITLE = 32, MAX_MESSAGE = 255, MAX_INLINE_LIST = 255;

export function DataValidationDialog({ open, mode, sheetName, initial, onDone, onClose }: {
  open: boolean;
  mode: 'new' | 'edit';
  sheetName: string;
  /** the rule being edited, or the defaults for a new one (its range = the selection) */
  initial: DvDefinition;
  onDone: (def: DvDefinition) => void;
  onClose: () => void;
}) {
  const [range, setRange] = useState('');
  const [type, setType] = useState('list');
  const [operator, setOperator] = useState('between');
  const [source, setSource] = useState('');
  const [v1, setV1] = useState('');
  const [v2, setV2] = useState('');
  const [allowBlank, setAllowBlank] = useState(true);
  const [dropdown, setDropdown] = useState(true);
  const [showInput, setShowInput] = useState(false);
  const [inputTitle, setInputTitle] = useState('');
  const [inputMessage, setInputMessage] = useState('');
  const [showError, setShowError] = useState(true);
  const [errorStyle, setErrorStyle] = useState('stop');
  const [errorTitle, setErrorTitle] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [problem, setProblem] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    const t = DV_TYPES.some(x => x.id === initial.type) ? initial.type : 'list';
    setRange(initial.range ? absoluteSqref(initial.range) : '');
    setType(t);
    setOperator(initial.operator || 'between');
    setSource(t === 'list' ? listSourceText(initial.formula1) : '');
    // A date shows in the date picker; a cell reference / formula stays as text.
    const shown = (v: string | null) => (t === 'date' ? serialToDate(v) ?? (v ? `=${v.replace(/^=/, '')}` : '') : v ?? '');
    setV1(t === 'list' ? '' : t === 'custom' ? (initial.formula1 ? `=${initial.formula1.replace(/^=/, '')}` : '') : shown(initial.formula1));
    setV2(shown(initial.formula2));
    setAllowBlank(initial.allowBlank);
    setDropdown(initial.inCellDropdown);
    setShowInput(initial.showInputMessage);
    setInputTitle(initial.inputTitle ?? '');
    setInputMessage(initial.inputMessage ?? '');
    setShowError(initial.showErrorMessage);
    setErrorStyle(initial.errorStyle || 'stop');
    setErrorTitle(initial.errorTitle ?? '');
    setErrorMessage(initial.errorMessage ?? '');
    setProblem(null);
  }, [open, initial]);

  const compares = type !== 'list' && type !== 'custom';
  const needsTwo = compares && (operator === 'between' || operator === 'notBetween');
  const isDate = type === 'date';

  /** One comparison value → Excel's stored form; a string = the problem to show. */
  const stored = (v: string, label: string): { value: string } | string => {
    const s = v.trim();
    if (!s) return `Enter the ${label}.`;
    if (s.startsWith('=')) return { value: s.slice(1).trim() };          // a cell reference / formula
    if (isDate) return dateToSerial(s) ? { value: dateToSerial(s)! } : `'${s}' is not a date.`;
    const n = Number(s);
    if (!Number.isFinite(n)) return `'${s}' is not a number.`;
    if ((type === 'whole' || type === 'textLength') && !Number.isInteger(n)) return `'${s}' is not a whole number.`;
    if (type === 'textLength' && n < 0) return 'A text length cannot be negative.';
    return { value: String(n) };
  };

  const done = () => {
    const sqref = normalizeSqref(range);
    if (!sqref) return setProblem('Enter the cells, e.g. =$G$11:$G$18 or =$G$11,$J$3.');
    let formula1: string | null = null, formula2: string | null = null;
    if (type === 'list') {
      formula1 = listFormula(source);
      if (!formula1) return setProblem('Enter the list values separated by commas, or a range such as =$AI$2:$AI$5.');
      if (formula1.startsWith('"') && formula1.length - 2 > MAX_INLINE_LIST)
        return setProblem(`A typed list can be at most ${MAX_INLINE_LIST} characters — put a longer list in cells and enter that range.`);
    } else if (type === 'custom') {
      formula1 = v1.trim().replace(/^=/, '').trim() || null;
      if (!formula1) return setProblem('Enter the formula the value must satisfy, e.g. =ISNUMBER(H11).');
    } else {
      const a = stored(v1, needsTwo ? 'minimum' : 'value');
      if (typeof a === 'string') return setProblem(a);
      formula1 = a.value;
      if (needsTwo) {
        const b = stored(v2, 'maximum');
        if (typeof b === 'string') return setProblem(b);
        formula2 = b.value;
      }
    }
    onDone({
      range: sqref, type, operator: compares ? operator : 'between', formula1, formula2,
      allowBlank, inCellDropdown: type === 'list' ? dropdown : true,
      showInputMessage: showInput, inputTitle: inputTitle.trim() || null, inputMessage: inputMessage.trim() || null,
      showErrorMessage: showError, errorStyle, errorTitle: errorTitle.trim() || null, errorMessage: errorMessage.trim() || null,
    });
  };

  const valueInput = (id: string, value: string, set: (v: string) => void) => (
    isDate && !value.startsWith('=')
      ? <input id={id} type="date" className="sd-input" value={value} onChange={e => set(e.target.value)} />
      : <input id={id} className="sd-input" value={value} onChange={e => set(e.target.value)}
          placeholder={isDate ? '=$C$2' : type === 'textLength' ? '20' : '0 or =$O$7'} />
  );

  return (
    <AppDialog open={open} title={`${mode === 'new' ? 'New' : 'Edit'} Data Validation — ${sheetName}`} icon="ti-checklist"
      width={600} onClose={onClose}
      footer={<>
        <button type="button" className="ca-btn" onClick={onClose}>Cancel</button>
        <button type="button" className="ca-btn ca-btn-solid" onClick={done}>OK</button>
      </>}>
      <div className="sd-grid sd-grid--side sd-grid--single stb-cf-form stb-dv-form">
        <div className="sd-row">
          <label className="sd-label" htmlFor="dv-range">Applies to<span className="req">*</span></label>
          <input id="dv-range" className="sd-input" value={range} onChange={e => setRange(e.target.value)} placeholder="=$G$11:$G$18" />
        </div>
        <div className="sd-row">
          <label className="sd-label" htmlFor="dv-type">Allow</label>
          <select id="dv-type" className="sd-input" value={type} onChange={e => { setType(e.target.value); setV1(''); setV2(''); }}>
            {DV_TYPES.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
          </select>
        </div>
        {type === 'list' && (<>
          <div className="sd-row">
            <label className="sd-label" htmlFor="dv-source">Source<span className="req">*</span></label>
            <input id="dv-source" className="sd-input" value={source} onChange={e => setSource(e.target.value)}
              placeholder="Absent, Present   or   =$AI$2:$AI$5" />
          </div>
          <div className="sd-row">
            <span className="sd-label" aria-hidden />
            <span className="stb-hint">Type the values separated by commas, or start with = to use a range of cells.</span>
          </div>
        </>)}
        {compares && (<>
          <div className="sd-row">
            <label className="sd-label" htmlFor="dv-op">Data</label>
            <select id="dv-op" className="sd-input" value={operator} onChange={e => setOperator(e.target.value)}>
              {DV_OPERATORS.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
            </select>
          </div>
          <div className="sd-row">
            <label className="sd-label" htmlFor="dv-v1">{needsTwo ? 'Minimum' : type === 'textLength' ? 'Length' : 'Value'}<span className="req">*</span></label>
            {valueInput('dv-v1', v1, setV1)}
          </div>
          {needsTwo && (
            <div className="sd-row">
              <label className="sd-label" htmlFor="dv-v2">Maximum<span className="req">*</span></label>
              {valueInput('dv-v2', v2, setV2)}
            </div>
          )}
        </>)}
        {type === 'custom' && (
          <div className="sd-row">
            <label className="sd-label" htmlFor="dv-formula">Formula<span className="req">*</span></label>
            <input id="dv-formula" className="sd-input" value={v1} onChange={e => setV1(e.target.value)} placeholder="=ISNUMBER(H11)" />
          </div>
        )}
        <div className="sd-row">
          <span className="sd-label">Options</span>
          <span className="stb-checks">
            <label><input type="checkbox" checked={allowBlank} onChange={e => setAllowBlank(e.target.checked)} /> Ignore blank</label>
            {type === 'list' && <label><input type="checkbox" checked={dropdown} onChange={e => setDropdown(e.target.checked)} /> In-cell dropdown</label>}
          </span>
        </div>

        <div className="stb-dv-section">Input Message</div>
        <div className="sd-row">
          <span className="sd-label" aria-hidden />
          <label className="stb-checks"><input type="checkbox" checked={showInput} onChange={e => setShowInput(e.target.checked)} /> Show input message when the cell is selected</label>
        </div>
        <div className="sd-row">
          <label className="sd-label" htmlFor="dv-in-title">Title</label>
          <input id="dv-in-title" className="sd-input" value={inputTitle} maxLength={MAX_TITLE} disabled={!showInput} onChange={e => setInputTitle(e.target.value)} />
        </div>
        <div className="sd-row">
          <label className="sd-label" htmlFor="dv-in-msg">Message</label>
          <input id="dv-in-msg" className="sd-input" value={inputMessage} maxLength={MAX_MESSAGE} disabled={!showInput} onChange={e => setInputMessage(e.target.value)} />
        </div>

        <div className="stb-dv-section">Error Alert</div>
        <div className="sd-row">
          <span className="sd-label" aria-hidden />
          <label className="stb-checks"><input type="checkbox" checked={showError} onChange={e => setShowError(e.target.checked)} /> Show error alert after invalid data is entered</label>
        </div>
        <div className="sd-row">
          <label className="sd-label" htmlFor="dv-err-style">Style</label>
          <select id="dv-err-style" className="sd-input" value={errorStyle} disabled={!showError} onChange={e => setErrorStyle(e.target.value)}>
            {DV_ERROR_STYLES.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
          </select>
        </div>
        <div className="sd-row">
          <label className="sd-label" htmlFor="dv-err-title">Title</label>
          <input id="dv-err-title" className="sd-input" value={errorTitle} maxLength={MAX_TITLE} disabled={!showError} onChange={e => setErrorTitle(e.target.value)} />
        </div>
        <div className="sd-row">
          <label className="sd-label" htmlFor="dv-err-msg">Message</label>
          <input id="dv-err-msg" className="sd-input" value={errorMessage} maxLength={MAX_MESSAGE} disabled={!showError} onChange={e => setErrorMessage(e.target.value)} />
        </div>
        {problem && <div className="sd-row"><span /><span className="sd-field-error">{problem}</span></div>}
      </div>
    </AppDialog>
  );
}
